import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    inject,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
    OperationsService,
    userSelectors,
} from '@wdx/clmi/api-services/services';
import {
    ActionType,
    EntityOperations,
    EntityPermissionType,
} from '@wdx/clmi/clmi-swagger-gen';
import { ModalManagementService } from '@wdx/clmi/ui';
import {
    WdxDialogComponent,
    WdxDialogService,
} from '@wdx/shared/components/wdx-dialog';
import { FileUploadPackage } from '@wdx/shared/infrastructure/file-io';
import {
    EntityAction,
    FeaturesService,
    NavItem,
    TRANSLATION_ACTION_NO_ACTIONS_AVAILABLE,
    TranslationsService,
    WdxSize,
} from '@wdx/shared/utils';
import { BehaviorSubject, Observable, combineLatest, forkJoin, of } from 'rxjs';
import {
    catchError,
    filter,
    finalize,
    shareReplay,
    take,
    takeUntil,
} from 'rxjs/operators';
import { BaseComponentClass } from '../../../../classes/base-component.class';
import { FilterHandler } from '../../../../classes/filter-handler.class';
import { ActionButton } from '../../../../models/action-button.model';
import { ActionButtonMode } from '../../../../models/action-buttons-mode.model';
import { DropdownButtonStyle } from '../../../../models/dropdown-button-style.model';
import { Privilege } from '../../../../models/privilege.model';
import { RandomStringPipe } from '../../../../pipes/random-string.pipe';
import * as rootReducer from '../../../../state/_setup/reducers';
import { MoleculeActionButtonService } from '../shared/services/molecule-action-button.service';
import * as R from 'ramda';
import {
    EntityIdActionsService,
    EntityIdPermissionsService,
} from '@wdx/clmi/clmi-swagger-gen/api-services';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'molecule-action-button',
    templateUrl: './molecule-action-button.component.html',
    standalone: false,
})
export class MoleculeActionButtonComponent
    extends BaseComponentClass
    implements OnInit, OnChanges
{
    private operationsService = inject(OperationsService);
    private entityIdPermissionsService = inject(EntityIdPermissionsService);
    private entityIdActionsService = inject(EntityIdActionsService);

    private _actionButton: ActionButton;
    @Input() set actionButton(value: ActionButton) {
        this.operations$ = undefined;
        this._actionButton = value;
    }
    public get actionButton() {
        return this._actionButton;
    }
    @Input() btnClass = 'btn-outline-primary';
    @Input() tooltip?: string;
    @Input() tooltipPlacement?: string;
    @Input() size: WdxSize = 'sm';
    @Input() parentContainer = 'body';
    @Input() hideDropdownChevron: boolean;
    @Input() dropdownButtonStyle: DropdownButtonStyle =
        DropdownButtonStyle.Label;
    @Input() dropdownPlacement = ['bottom-right', 'top-right'];
    @Input() dropdownMenuSize = 'xs';
    @Input() dropdownAutoClose: boolean | 'outside' | 'inside';
    @Input() isDisabled = false;
    @Input() filterHandler: FilterHandler;
    @Input() highlightSelectedMenuItem: boolean;
    @Input() cySelector?: string;

    @Output() selectMenuItem = new EventEmitter<NavItem>();
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onClick = new EventEmitter<boolean>();
    @Output() fileSelected = new EventEmitter<FileUploadPackage>();
    @Output() dropdownMenuToggled = new EventEmitter<boolean>();

    dropdownTemplateId: string;
    dropdownMenu$ = new BehaviorSubject<NavItem[]>(null);

    operations$: Observable<EntityOperations>;
    isLoading$ = new BehaviorSubject(false);
    isVisible = true;
    resetButtonsFlag = false;

    private moleculeActionButtonService = inject(MoleculeActionButtonService);
    private wdxDialogService = inject(WdxDialogService);
    private translationsService = inject(TranslationsService);

    constructor(
        private changeDetector: ChangeDetectorRef,
        private featuresService: FeaturesService,
        store$: Store<rootReducer.State>,
        modalManagementService: ModalManagementService,
    ) {
        super();
        this.patchInjectedItems({
            store$,
            modalManagementService,
        });
    }

    ngOnChanges() {
        if (this.actionButton?.dropdownMenu) {
            this.setDropdownMenu();
        }

        if (this.actionButton?.permission) {
            this.setPermissionVisibility();
        }

        if (this.actionButton?.privileges) {
            this.setPrivilegeVisibility();
        }

        if (this.actionButton?.feature) {
            this.isVisible = this.featuresService.hasFeature(
                this.actionButton?.feature,
            );
        }
    }

    ngOnInit(): void {
        this?.filterHandler?.resetValues
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.resetButtonsFlag = false;

                if (
                    this.actionButton.mode === ActionButtonMode.FavouriteButton
                ) {
                    this.changeDetector.detectChanges();
                    this.resetButtonsFlag = true;
                }
            });
    }

    setDropdownMenu() {
        this.dropdownTemplateId = new RandomStringPipe().transform();
        this.dropdownMenu$.next(this.actionButton.dropdownMenu);
    }

    setPermissionVisibility() {
        if (this.actionButton?.operationsSetup) {
            this.isVisible = false;
            if (!this.operations$) {
                this.getOperations();
            }
            this.operations$
                .pipe(
                    takeUntil(this.destroyed$),
                    filter((operations) => Boolean(operations?.permissions)),
                )
                .subscribe(
                    (operations) =>
                        (this.isVisible = operations.permissions.includes(
                            this.actionButton?.permission,
                        )),
                );
        }
    }

    setPrivilegeVisibility() {
        this.isVisible = false;
        this.store$
            .select(userSelectors.getPrivilegesSelector)
            .pipe(take(1))
            .subscribe(
                (privileges) =>
                    (this.isVisible =
                        privileges?.includes(Privilege.Administrator) ||
                        privileges?.some((privilege: Privilege) =>
                            this.actionButton?.privileges?.includes(privilege),
                        )),
            );
    }

    getOperations() {
        this.operations$ = undefined;
        const { operationsSetup } = this.actionButton;
        if (
            operationsSetup &&
            operationsSetup?.entityType &&
            operationsSetup?.entityId
        ) {
            this.isLoading$.next(true);
            const { entityType, entityId } = operationsSetup;
            if (
                this.entityIdPermissionsService.hasTypeService(entityType) &&
                this.entityIdActionsService.hasTypeService(entityType)
            ) {
                this.operations$ = forkJoin({
                    permissions: this.entityIdPermissionsService.get(
                        entityType,
                        entityId,
                    ),
                    actions: this.entityIdActionsService.get(
                        entityType,
                        entityId,
                    ),
                }).pipe(
                    finalize(() => this.isLoading$.next(false)),
                    shareReplay(1),
                );
            } else {
                this.operations$ = this.operationsService
                    .getOperationsForId(entityType, entityId)
                    .pipe(
                        finalize(() => this.isLoading$.next(false)),
                        shareReplay(1),
                    );
            }
        } else if (operationsSetup && operationsSetup?.entityType) {
            this.isLoading$.next(true);
            const { entityType } = operationsSetup;
            this.operations$ = this.operationsService
                .getOperationsForEntity(entityType)
                .pipe(
                    finalize(() => this.isLoading$.next(false)),
                    shareReplay(1),
                );
        } else {
            this.isLoading$.next(false);
            this.operations$ = of({
                permissions: [EntityPermissionType.Edit],
                actions: [],
            }).pipe(shareReplay(1));
        }
    }

    onFileSelected(uploadEvent: any): void {
        const fileUploadPackage = {
            id: new RandomStringPipe().transform(),
            file: uploadEvent.target.files[0],
        };
        this.fileSelected.emit(fileUploadPackage);
    }

    onActionButtonClicked(toggle?: boolean) {
        if (this.actionButton.modalId) {
            this.modalManagementService.openModalWithId(
                this.actionButton.modalId,
            );
        }
        if (this.actionButton.callback) {
            this.actionButton.callback();
        }
        this.onClick.emit(toggle);
    }

    onMenuItemSelected(navItem: NavItem) {
        if (navItem.callback) {
            navItem.callback();
        }
        this.selectMenuItem.emit(navItem);
    }

    onDropdownToggled(isOpen: boolean) {
        this.dropdownMenuToggled.emit(isOpen);
        if (isOpen) {
            this.getOperations();
            combineLatest([
                this.store$.select(userSelectors.getPrivilegesSelector),
                this.operations$,
            ])
                .pipe(takeUntil(this.destroyed$))
                .subscribe(([privileges, operations]) => {
                    const updatedMenu = this.actionButton?.dropdownMenu?.map(
                        (menu) => ({
                            ...menu,
                            hidden: (() => {
                                if (menu.hidden) {
                                    return true;
                                }

                                if (menu.feature) {
                                    return !this.featuresService.hasFeature(
                                        menu.feature,
                                    );
                                }

                                if (
                                    menu.privilege &&
                                    !privileges.includes(
                                        Privilege.Administrator,
                                    )
                                ) {
                                    return !privileges?.includes(
                                        menu.privilege,
                                    );
                                }
                                if (menu.permission) {
                                    if (
                                        menu.notPermission &&
                                        operations?.permissions?.includes(
                                            menu.notPermission,
                                        )
                                    ) {
                                        return true;
                                    }
                                    return !operations?.permissions?.includes(
                                        menu.permission,
                                    );
                                }
                                return false;
                            })(),
                        }),
                    );
                    let actionMenu;
                    if (
                        !this.actionButton?.ignoreActions &&
                        operations?.actions?.length
                    ) {
                        actionMenu = operations.actions.map((action) =>
                            this.mapActionEntityToNavItem(action, updatedMenu),
                        );

                        // ---------------------------------------------------------
                        // Adding ability to override action item
                        // To be removed once the actions endpoint has been updated
                        // to do this automatically
                        // ---------------------------------------------------------
                        actionMenu.forEach((actionItem) => {
                            const matchIndex = updatedMenu?.findIndex(
                                (updatedItem) =>
                                    R.isNotNil(updatedItem.id) &&
                                    R.isNotNil(actionItem.id) &&
                                    updatedItem.id === actionItem.id,
                            );
                            if (matchIndex > -1) {
                                actionItem.hidden = true;
                                updatedMenu[matchIndex].hidden = false;
                            }
                        });
                        // ---------------------------------------------------------
                    }
                    if (!actionMenu && !updatedMenu) {
                        const emptyActionLabel: NavItem = {
                            id: 'no-actions-label',
                            label: this.translationsService.getTranslationByKey(
                                TRANSLATION_ACTION_NO_ACTIONS_AVAILABLE,
                            ),
                            disabled: true,
                            hidden: false,
                        };
                        actionMenu = [emptyActionLabel];
                    }
                    this.dropdownMenu$.next([
                        ...(updatedMenu || []),
                        ...(actionMenu || []),
                    ]);
                });
        }
    }

    private dispatchOperationAction(action: EntityAction): void {
        this.moleculeActionButtonService.operationActionCallback(
            this.actionButton.operationsSetup,
            action,
            this.actionButton.actionCallbacks?.find(
                (cb) =>
                    cb.type === action.type &&
                    (!cb.url || cb.url === action.url),
            )?.callback,
        );
    }

    private mapActionEntityToNavItem(
        action: EntityAction,
        existingMenuOptions: NavItem[],
    ) {
        const navItem: NavItem = {
            id: action.displayName?.key,
            label: this.translationsService.translateTokenisedString(
                action.displayName?.value || action.displayName?.key,
            ),
            icon: action.icon,
        };
        let hiddenForminitialisationParams = {};

        const matchingItem = existingMenuOptions?.find(
            (item) => item?.label === action?.displayName?.value,
        );
        if (action.type === ActionType.Group) {
            if (matchingItem) {
                matchingItem.hidden = true;
            }
            return {
                ...navItem,
                linkedMenu: action.children.map((item) =>
                    this.mapActionEntityToNavItem(item, []),
                ),
            };
        }
        if (
            action.type === ActionType.Form ||
            action.type === ActionType.NewForm
        ) {
            // hide existing matching form
            existingMenuOptions?.forEach((item) => {
                if (
                    item?.formSetup?.formId &&
                    item.formSetup.formId === action.formName
                ) {
                    item.hidden = true;
                    hiddenForminitialisationParams =
                        item.formSetup?.initialisationParams ?? {};
                }
            });
        }
        if (action.type === ActionType.Form) {
            return {
                ...navItem,
                formSetup: {
                    formId: action.formName,
                    entityId:
                        action.formDataEntityId ||
                        this.actionButton.operationsSetup?.entityId,
                },
            };
        }
        if (action.type === ActionType.NewForm) {
            const initParams =
                action.formInitialisationParameters ||
                this.actionButton.operationsSetup?.entityId
                    ? {
                          initialisationParams: {
                              ...hiddenForminitialisationParams,
                              ...(action.formInitialisationParameters || {}),
                              ...(this.actionButton.operationsSetup?.entityId
                                  ? {
                                        entityId:
                                            this.actionButton.operationsSetup
                                                ?.entityId,
                                    }
                                  : {}),
                          },
                      }
                    : {
                          initialisationParams: {
                              ...hiddenForminitialisationParams,
                          },
                      };

            return {
                ...navItem,
                formSetup: {
                    formId: action.formName || action.url,
                    ...initParams,
                },
            };
        }
        if (action.type === ActionType.Patch) {
            if (matchingItem) {
                matchingItem.hidden = true;
            }

            return {
                ...navItem,
                callback: () => {
                    if (action?.url) {
                        if (action?.requiresConfirmation) {
                            const actionModal =
                                this.wdxDialogService.open(WdxDialogComponent);
                            actionModal.componentInstance.title =
                                this.translationsService.translateTokenisedString(
                                    `${action.displayName.key}_CONFIRMATION_TITLE`,
                                );

                            actionModal.componentInstance.dialogBody =
                                this.translationsService.translateTokenisedString(
                                    `${action.displayName.key}_CONFIRMATION_BODY`,
                                );

                            actionModal.componentInstance.wdxOnClick.subscribe(
                                () => {
                                    this.dispatchOperationAction(action);
                                },
                            );
                        } else {
                            this.dispatchOperationAction(action);
                        }
                    }
                },
                data: action,
            };
        }
    }
}
